import React from 'react';
import { Badge, NumberInput, Table } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { isTenths, num } from '../../lib/aiPricingView';
import {
  Code, CountCell, MarginBadge, ParsehCell, UsdCell, useEnumLabel, useServiceLabel,
} from './shared';

/**
 * The prices a policy produces, one row per service (design §12.6).
 *
 * The override column is an INLINE `NumberInput` on a draft: type a Parseh
 * figure and leave the field to persist it (`setAiServiceOverride`); clear it
 * to remove the override (`parseh: null`). On an active or archived policy the
 * column is read-only — the policy is immutable — and on an unsaved draft it is
 * disabled with the reason, because there is no policy row to attach it to.
 */
export default function PolicyResultsTable({ prices, canOverride, overrideDisabledReason, onOverride, pendingKey }) {
  const { t, formatNumber } = useTranslation();
  const label = useServiceLabel();
  const basisLabel = useEnumLabel('aiPricing.services.drawer.basisSource');

  const columns = [
    {
      key: 'service',
      header: t('aiPricing.policies.editor.results.columns.service'),
      render: (p) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground">{label(p)}</div>
          <div className="truncate text-xs text-muted-foreground"><Code>{p.serviceKey}</Code></div>
        </div>
      ),
    },
    {
      key: 'basis',
      header: t('aiPricing.policies.editor.results.columns.basis'),
      align: 'end',
      width: '11rem',
      render: (p) => (
        <span className="inline-flex flex-col items-end gap-0.5">
          <UsdCell value={p.basisCostUsd} />
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            {p.basisSource === 'declared' ? <Badge variant="warning">{t('aiPricing.policies.editor.results.badge.declared')}</Badge>
              : p.basisSource === 'none' || num(p.basisCostUsd) == null ? <Badge variant="destructive">{t('aiPricing.policies.editor.results.badge.none')}</Badge>
                : <span>{basisLabel(p.basisSource)}</span>}
            <span className="tabular-nums">{t('aiPricing.policies.editor.results.sample', { count: p.basisSample || 0 })}</span>
          </span>
        </span>
      ),
    },
    { key: 'costParseh', header: t('aiPricing.policies.editor.results.columns.costParseh'), align: 'end', width: '7rem', render: (p) => <ParsehCell value={p.costParseh} /> },
    { key: 'computed', header: t('aiPricing.policies.editor.results.columns.computed'), align: 'end', width: '7rem', render: (p) => <ParsehCell value={p.computedParseh} /> },
    {
      key: 'override',
      header: t('aiPricing.policies.editor.results.columns.override'),
      align: 'end',
      width: '9rem',
      render: (p) => (
        <OverrideCell
          price={p}
          disabled={!canOverride}
          reason={overrideDisabledReason}
          pending={pendingKey === p.serviceKey}
          onCommit={(value) => onOverride(p.serviceKey, value)}
          ariaLabel={t('aiPricing.policies.editor.results.overrideLabel', { service: label(p) })}
        />
      ),
    },
    {
      key: 'effective',
      header: t('aiPricing.policies.editor.results.columns.effective'),
      align: 'end',
      width: '8rem',
      render: (p) => <ParsehCell value={p.effectiveParseh} suffix={p.unit === 'minute' ? t('aiPricing.services.perMinute') : ''} className="font-semibold" />,
    },
    { key: 'margin', header: t('aiPricing.policies.editor.results.columns.margin'), align: 'end', width: '6rem', render: (p) => <MarginBadge value={p.marginPct} data-testid={`ai-policy-row-${p.serviceKey}-margin`} /> },
    { key: 'active', header: t('aiPricing.policies.editor.results.columns.active'), align: 'end', width: '7rem', render: (p) => <ParsehCell value={p.activeParseh} /> },
    {
      key: 'delta',
      header: t('aiPricing.policies.editor.results.columns.delta'),
      align: 'end',
      width: '5rem',
      render: (p) => {
        const d = num(p.deltaParseh);
        if (d == null || d === 0) return <span className="text-muted-foreground">{d === 0 ? formatNumber(0) : t('common.value.none')}</span>;
        return (
          <Badge variant={d > 0 ? 'warning' : 'success'} className="tabular-nums">
            {formatNumber(d, { maximumFractionDigits: 2, signDisplay: 'always' })}
          </Badge>
        );
      },
    },
  ];

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.policies.editor.results.aria')}
      columns={columns}
      rows={prices}
      rowKey={(p) => p.serviceKey}
      rowProps={(p) => ({ 'data-testid': `ai-policy-row-${p.serviceKey}`, 'data-delta': num(p.deltaParseh) || 0 })}
      empty={<p className="p-6 text-center text-sm text-muted-foreground" data-testid="ai-policy-results-empty">{t('aiPricing.policies.editor.results.empty')}</p>}
      data-testid="ai-policy-results"
    />
  );
}

/**
 * A price is a whole number of tenths (PM-0427): «3» or «3.1», never «3.21».
 * The service refuses anything finer; the field says so first and keeps the
 * typed figure, rather than sending it to fail.
 */
function OverrideCell({ price, disabled, reason, pending, onCommit, ariaLabel }) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState(price.overrideParseh ?? '');
  const [invalid, setInvalid] = React.useState(false);
  React.useEffect(() => { setDraft(price.overrideParseh ?? ''); setInvalid(false); }, [price.overrideParseh]);

  const commit = () => {
    const next = String(draft).trim();
    const current = price.overrideParseh == null ? '' : String(price.overrideParseh);
    if (!isTenths(next)) { setInvalid(true); return; }
    setInvalid(false);
    if (num(next) === num(current)) return;
    onCommit(next === '' ? null : next);
  };

  return (
    <div className="w-24" title={disabled ? reason : undefined} data-testid={`ai-policy-row-${price.serviceKey}-override`}>
      <NumberInput
        value={draft}
        onChange={(e) => { setDraft(e.target?.value ?? e); if (invalid) setInvalid(false); }}
        onBlur={commit}
        error={invalid ? t('aiPricing.policies.editor.tenthsOnly') : undefined}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        placeholder={t('aiPricing.policies.editor.results.overridePlaceholder')}
        disabled={disabled || pending}
        aria-label={ariaLabel}
        aria-busy={pending || undefined}
        style={{ margin: 0 }}
      />
    </div>
  );
}
